import { useState } from 'react';
import { FileText, Download, Loader2, CheckCircle2, AlertCircle, Calendar, Hash } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { useEmployees } from '../hooks/useEmployees';
import {
  useRequestDocument,
  useAllDocumentsAdmin,
  type DocumentType,
  type RequestDocumentResponse,
} from '../hooks/documents/useDocuments';
import { apiClient } from '../lib/api';

export function Documents() {
  // Form state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [documentType, setDocumentType] = useState<DocumentType>('PAYSLIP');
  const [period, setPeriod] = useState<string>('');
  const [documentId, setDocumentId] = useState<string>('');

  // Result state
  const [requestResult, setRequestResult] = useState<RequestDocumentResponse | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Hooks
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees({ limit: 1000, isActive: true });
  const requestDocument = useRequestDocument();
  const { data: allDocuments, isLoading: isLoadingDocuments } = useAllDocumentsAdmin({ limit: 10 });

  // Reset form
  const handleReset = () => {
    setRequestResult(null);
    setRequestError(null);
    setSelectedEmployeeId('');
    setPeriod('');
    setDocumentId('');
  };

  // Request document
  const handleRequestDocument = async () => {
    if (!selectedEmployeeId) {
      setRequestError('Выберите сотрудника');
      return;
    }

    // Validate required fields
    if (documentType === 'PAYSLIP' && !period) {
      setRequestError('Укажите период (ГГГГ-ММ) для расчётного листка');
      return;
    }

    if ((documentType === 'ORDER' || documentType === 'CERTIFICATE') && !documentId) {
      setRequestError('Укажите ID документа');
      return;
    }

    setRequestError(null);
    setRequestResult(null);

    try {
      const result = await requestDocument.mutateAsync({
        employeeId: selectedEmployeeId,
        documentType,
        period: documentType === 'PAYSLIP' ? period : undefined,
        documentId: documentType === 'ORDER' || documentType === 'CERTIFICATE' ? documentId : undefined,
      });

      setRequestResult(result);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Не удалось получить документ из 1С';
      setRequestError(errorMessage);
    }
  };

  // Download document (Admin)
  const handleDownload = async (docId: string) => {
    try {
      // Получаем signed URL из backend
      const { url } = await apiClient.downloadDocumentAdmin(docId);

      // Открываем signed URL (он уже публичный и не требует headers)
      window.open(url, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Не удалось скачать документ';
      alert(`Ошибка скачивания: ${errorMessage}`);
    }
  };

  // Get selected employee name
  const selectedEmployee = employeesData?.data.find((e) => e.id === selectedEmployeeId);
  const selectedEmployeeName = selectedEmployee
    ? `${selectedEmployee.last_name} ${selectedEmployee.first_name} ${selectedEmployee.middle_name || ''}`.trim()
    : '';

  // Document type labels
  const documentTypeLabels: Record<DocumentType, string> = {
    PAYSLIP: 'Расчётный листок',
    ORDER: 'Приказ',
    CERTIFICATE: 'Справка',
    CONTRACT: 'Трудовой договор',
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs />

      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-8 w-8" />
          Документы из 1С
        </h1>
        <p className="text-muted-foreground mt-2">
          Запрос документов сотрудников из системы 1С с кэшированием
        </p>
      </div>

      {/* Request Form */}
      <Card>
        <CardHeader>
          <CardTitle>Запросить документ</CardTitle>
          <CardDescription>
            Выберите сотрудника и тип документа. Документ будет получен из 1С или вернётся из кэша.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Employee Select */}
          <div className="space-y-2">
            <Label htmlFor="employee">Сотрудник</Label>
            <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
              <SelectTrigger id="employee" disabled={isLoadingEmployees}>
                <SelectValue placeholder="Выберите сотрудника..." />
              </SelectTrigger>
              <SelectContent>
                {employeesData?.data.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {`${employee.last_name} ${employee.first_name} ${employee.middle_name || ''}`.trim()} (#{employee.employee_number})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEmployee && !selectedEmployee.code_1c && (
              <p className="text-sm text-destructive">
                ⚠️ У сотрудника нет code_1c - генерация документов невозможна
              </p>
            )}
          </div>

          {/* Document Type Select */}
          <div className="space-y-2">
            <Label htmlFor="document-type">Тип документа</Label>
            <Select
              value={documentType}
              onValueChange={(value) => setDocumentType(value as DocumentType)}
            >
              <SelectTrigger id="document-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAYSLIP">Расчётный листок</SelectItem>
                <SelectItem value="ORDER">Приказ</SelectItem>
                <SelectItem value="CERTIFICATE">Справка</SelectItem>
                <SelectItem value="CONTRACT">Трудовой договор</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional fields */}
          {documentType === 'PAYSLIP' && (
            <div className="space-y-2">
              <Label htmlFor="period" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Период (ГГГГ-ММ)
              </Label>
              <Input
                id="period"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value.replace('-', '-'))}
                placeholder="2025-05"
              />
              <p className="text-xs text-muted-foreground">
                Формат: ГГГГ-ММ (например, 2025-05 для мая 2025)
              </p>
            </div>
          )}

          {(documentType === 'ORDER' || documentType === 'CERTIFICATE') && (
            <div className="space-y-2">
              <Label htmlFor="document-id" className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                ID документа в 1С
              </Label>
              <Input
                id="document-id"
                type="text"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                placeholder="ORDER-2024-001"
              />
              <p className="text-xs text-muted-foreground">
                {documentType === 'ORDER'
                  ? 'Номер приказа в системе 1С (например, ORDER-2024-001)'
                  : 'Номер справки в системе 1С (например, CERT-2024-067)'}
              </p>
            </div>
          )}

          {/* Error Message */}
          {requestError && (
            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900">Ошибка</h3>
                  <p className="text-sm text-red-700 mt-1">{requestError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {requestResult && (
            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900">Документ получен!</h3>
                  <div className="space-y-2 mt-2 text-sm text-green-800">
                    <p>
                      <strong>Сотрудник:</strong> {selectedEmployeeName}
                    </p>
                    <p>
                      <strong>Тип:</strong> {documentTypeLabels[documentType]}
                    </p>
                    {requestResult.fromCache && (
                      <p className="text-green-700">
                        📦 Документ получен из кэша (быстро!)
                      </p>
                    )}
                    {!requestResult.fromCache && (
                      <p className="text-green-700">
                        🆕 Документ сгенерирован в 1С (новый)
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleDownload(requestResult.id)}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Скачать документ
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleReset}>
                        Запросить ещё
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleRequestDocument}
              disabled={requestDocument.isPending || !selectedEmployeeId}
              className="gap-2"
            >
              {requestDocument.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {requestDocument.isPending ? 'Запрос к 1С...' : 'Запросить документ'}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Сбросить
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Documents */}
      <Card>
        <CardHeader>
          <CardTitle>Последние документы</CardTitle>
          <CardDescription>Список последних запрошенных документов</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingDocuments && (
            <div className="text-center py-4 text-muted-foreground">Загрузка...</div>
          )}

          {!isLoadingDocuments && allDocuments?.data && allDocuments.data.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>Документы ещё не запрашивались</p>
            </div>
          )}

          {!isLoadingDocuments && allDocuments?.data && allDocuments.data.length > 0 && (
            <div className="space-y-2">
              {allDocuments.data.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium">{doc.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {doc.employee &&
                        `${doc.employee.last_name} ${doc.employee.first_name} ${doc.employee.middle_name || ''}`.trim()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(doc.createdAt).toLocaleString('ru-RU')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(doc.id)}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Скачать
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
